/**
 * What the Share dialog holds until «Apply», as the app's desktop `SharingPopupPresenter` defers roles and link access:
 * staged values, the order they are sent in, and what stays staged after a failure.
 */
import type { LinkAccess, SharePerson, ShareRole, ShareSource, ShareState } from './source';

type MemberRole = Exclude<ShareRole, 'owner'>;
export interface ShareDraft {
  link: LinkAccess | null;
  roles: ReadonlyMap<string, MemberRole>;
  removed: ReadonlySet<string>;
  /** The changes the last «Apply» could not make: a person's id, or `link`. */
  failed: ReadonlySet<string>;
}

export const EMPTY_DRAFT: ShareDraft = { link: null, roles: new Map(), removed: new Set(), failed: new Set() };

const committedRole = (state: ShareState, id: string) => state.people.find(person => person.id === id)?.role;

export function stageLink(draft: ShareDraft, state: ShareState, link: LinkAccess): ShareDraft {
  return { ...draft, link: link === state.link ? null : link, failed: without(draft.failed, 'link') };
}

/** Choosing the committed role again unstages it and undoes a staged removal. */
export function stageRole(draft: ShareDraft, state: ShareState, id: string, role: MemberRole): ShareDraft {
  const roles = new Map(draft.roles);
  if (committedRole(state, id) === role) roles.delete(id); else roles.set(id, role);
  return { ...draft, roles, removed: without(draft.removed, id), failed: without(draft.failed, id) };
}

export function stageRemove(draft: ShareDraft, id: string): ShareDraft {
  const roles = new Map(draft.roles);
  roles.delete(id);
  return { ...draft, roles, removed: new Set([...draft.removed, id]), failed: without(draft.failed, id) };
}

export const hasChanges = (draft: ShareDraft): boolean => draft.link !== null || draft.roles.size > 0 || draft.removed.size > 0;
export const shownRole = (draft: ShareDraft, person: SharePerson): ShareRole => draft.roles.get(person.id) ?? person.role;
export const shownLink = (draft: ShareDraft, state: ShareState): LinkAccess => draft.link ?? state.link;

/** A fresh list: a staged value it already has unstages, a person no longer there drops out, the rest stays. */
export function reconcile(draft: ShareDraft, state: ShareState): ShareDraft {
  if (!hasChanges(draft) && !draft.failed.size) return draft;
  const link = draft.link === state.link ? null : draft.link;
  const here = new Set(state.people.map(person => person.id));
  const roles = new Map([...draft.roles].filter(([id, role]) => here.has(id) && committedRole(state, id) !== role));
  const removed = new Set([...draft.removed].filter(id => here.has(id)));
  const failed = new Set([...draft.failed].filter(id => id === 'link' ? link !== null : roles.has(id) || removed.has(id)));
  return { link, roles, removed, failed };
}

/** Sends the link, then the roles, then the removals, one at a time; reads the list once at the end. Returns what is left. */
export async function applyDraft(draft: ShareDraft, source: ShareSource): Promise<ShareDraft> {
  const failed = new Set<string>();
  const attempt = async (key: string, send: () => Promise<void>) => { try { await send(); } catch { failed.add(key); } };
  const alreadyGone = (error: unknown) => (error as { code?: string } | null)?.code === 'gone';
  if (draft.link !== null) await attempt('link', () => source.setLink(draft.link!));
  for (const [id, role] of draft.roles) await attempt(id, () => source.setRole(id, role));
  for (const id of draft.removed) await attempt(id, () => source.remove(id).catch(error => { if (!alreadyGone(error)) throw error; }));
  const left: ShareDraft = { link: failed.has('link') ? draft.link : null, roles: new Map([...draft.roles].filter(([id]) => failed.has(id))),
    removed: new Set([...draft.removed].filter(id => failed.has(id))), failed };
  await source.refresh();
  return reconcile(left, source.get());
}

function without<T>(set: ReadonlySet<T>, value: T): ReadonlySet<T> {
  if (!set.has(value)) return set;
  const next = new Set(set);
  next.delete(value);
  return next;
}
